import * as APP_CONFIG from 'configs/Config';

const getToken = () => {
  return localStorage.getItem("AUTH_TOKEN");
};

const fetchWrapper = async (endpoint, options = {}) => {
    const token = getToken();
    const flag = endpoint.startsWith('/api/');
    // BACKGROUND POLLS DO NOT GET TO END THE SESSION.
    // The force-logout below clears localStorage and replaces the location. That is the right
    // answer to a request the USER made — they asked for something, their session is gone,
    // they are told. It is the wrong answer to a request a TIMER made: the incoming-v2 board
    // polls /changes every 15 s, so a JWT expiring while a coordinator sits on a record sheet
    // with unsaved edits ejects them mid-sentence, triggered by nothing they did, with no
    // chance to save. `backgroundPoll: true` suppresses ONLY the redirect, ONLY for that call.
    // The 4xx is still returned to the caller, which reads it as `denied`, stops its channel
    // and says so in the badge; the session end then surfaces on the next foreground request,
    // where a logout is an answer to something the user actually asked for.
    //
    // Opt-IN, deliberately: this is the default export for ~100 call sites and every one of
    // them omits the flag, so foreground logout behaviour is unchanged by construction. It is
    // also destructured OUT of the options handed to fetch(), so it can never reach the wire.
    const { backgroundPoll, ...fetchInit } = options;
    // REACT_APP_FETCHWRAPPER_STRICT gates two fetchWrapper hardenings: the network-error
    // body shape (catch, below) AND the logout policy. The legacy policy logs out on any
    // 401||403, which force-logged-out staff on a per-request 403 (e.g. an admin reset that
    // orgScope-denied: onResetPassword -> /api/user/reset_password runs through THIS client).
    // But reven-api authenticate.js returns 403 for a DEAD SESSION too (expired/invalid token),
    // so a blanket "401-only" (the first attempt at this fix) would NEVER log out an expired
    // session. When ON, disambiguate a 403 by its body message: log out only on a dead session
    // ("Token is expired"/"Invalid token"), keep the session on an authz-deny ("Forbidden: ...").
    // Flag-OFF == today's exact 401||403. This couples the client to reven-api's error strings;
    // the cleaner long-term fix is authenticate.js returning 401 for expired/invalid tokens
    // (then this reduces to plain 401-only). The axios FetchInterceptor (#32) needs the same.
    const strict = process.env.REACT_APP_FETCHWRAPPER_STRICT === 'true';
    const fullUrl = flag ? `${APP_CONFIG.BACKEND_SERVER}${endpoint}` : endpoint;

    const headers = {
        ...fetchInit.headers,
    };

    if (flag) {
        headers['Content-Type'] = 'application/json';
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }

    const fetchOptions = {
        ...fetchInit,
        headers,
    };

    try {
        const response = await fetch(fullUrl, fetchOptions);

        if (!response.ok && flag) {
            // 401 only. reven-api answers 401 for a dead session (missing, expired
            // or invalid token) and reserves 403 for "authenticated, but not
            // allowed" -- so the status alone is sufficient and we no longer sniff
            // the response body or match on the server's error strings. (main, #45)
            let forceLogout = response.status === 401;

            // Suppression applied HERE, after the verdict, so a background poll
            // computes the SAME verdict a foreground call would -- it simply does
            // not act on it by unloading the page. The caller still receives the
            // response and decides for itself. (incoming-v2 lane)
            //
            // Rebase note: main's 401-only policy and this lane's backgroundPoll
            // opt-out are independent, and BOTH are kept. The lane was written
            // against the older body-sniffing 403 branch that #45 deleted -- that
            // branch is gone; the opt-out is not.
            if (forceLogout && backgroundPoll) {
                forceLogout = false;
            }
            if (forceLogout) {
                localStorage.removeItem("AUTH_TOKEN");
                localStorage.removeItem("USER_ID");
                localStorage.removeItem("LOCKSCREEN");
                localStorage.removeItem("USER_DETAILS");
                localStorage.removeItem("MAP_DETAILS");
                localStorage.removeItem("ORG_DETAILS");
                window.location.replace("/auth/login");
            }
        }

        return response;
    } catch (error) {
        console.error('Fetch error:', error);
        // The fabricated network-error response used `json: async () => null`, so the
        // ~100 callers that read fields off `await res.json()` crashed on null and
        // blanked the screen (audit #2). With REACT_APP_FETCHWRAPPER_STRICT ON, return
        // an empty-object body instead of null so those reads yield undefined, not a
        // crash; .ok stays false so ok-checking callers still see failure. Flag-OFF ==
        // today's exact behavior (null). `strict` is read once at the top of the function.
        return {
            ok: false,
            status: 0,
            json: async () => (strict ? {} : null),
        };
    }
};

export default fetchWrapper;
